<ng-container>
  @if (organizationName$ | async; as orgName) {
    <div class="catalog-org-banner">
      <mat-icon class="catalog-org-icon">corporate_fare</mat-icon>
      <div class="catalog-org-text">
        <span class="catalog-org-label">{{ 'catalog.overview.catalogOfPrefix' | transloco }}</span>
        <span class="catalog-org-name">{{ orgName }}</span>
      </div>
    </div>
  }
  @if (sortedDatasets().length) {
    <div class="cards-toolbar">
      <span class="cards-count">{{ sortedDatasets().length }} {{ (sortedDatasets().length === 1 ? 'sort.item' : 'sort.items') | transloco }}</span>
      <app-sort-dropdown
        [options]="datasetSortOptions"
        [selectedField]="datasetSortField()"
        [selectedDirection]="datasetSortDirection()"
        (sortChanged)="onDatasetSortChanged($event)" />
    </div>
    <div class="entity-cards-wrapper">
      @for (catalogEntry of sortedDatasets(); track catalogEntry.id) {
        <app-service-offer-entity-card
          [title]="catalogEntry.serviceOfferName ?? catalogEntry.name"
          [serviceType]="catalogEntry.serviceType"
          [serviceID]="catalogEntry.id"
          [description]="catalogEntry.description ?? catalogEntry.name"
          [language]="catalogEntry.language"
          [publisher]="catalogEntry.publisher"
          [sovereign]="catalogEntry.sovereign"
          [dataOfferSource]="catalogEntry.dataOfferSource"
          [datasource]="catalogEntry.datasource"
          [method]="catalogEntry.method"
          [baseUrl]="catalogEntry.baseUrl"
          [keywords]="catalogEntry.keywords"
          [subCategory]="catalogEntry.subCategory"
          [showEditDeleteButtons]="false"
          (titleClick)="openCatalogDetailPage(catalogEntry)"
        ></app-service-offer-entity-card>
      }
    </div>
  } @else if ((loading$ | async) === false) {
    <div class="catalog-empty-state">
      <div class="empty-state-icon-ring">
        <mat-icon class="empty-state-icon">{{ (hasSearched$ | async) ? 'search_off' : 'inventory_2' }}</mat-icon>
      </div>
      @if (hasSearched$ | async) {
        <span class="empty-state-heading">{{ 'catalog.overview.emptyState.searchTitle' | transloco }}</span>
        <span class="empty-state-description">{{ 'catalog.overview.emptyState.searchDescription' | transloco }}</span>
      } @else {
        <span class="empty-state-heading">{{ 'catalog.overview.emptyState.title' | transloco }}</span>
        <span class="empty-state-description">{{ 'catalog.overview.emptyState.description' | transloco }}</span>
      }
    </div>
  }
</ng-container>

<!-- ═══════════════════════════════════════════════════════════ -->
<!--  Slide-Over Panel: Flat Catalog Detail Body                -->
<!-- ═══════════════════════════════════════════════════════════ -->
<ng-template #panelBody>
  <ng-container *transloco="let t">
    @if (panelLoading()) {
      <div class="so-loading">
        <div class="so-loading-pulse"></div>
        <div class="so-loading-pulse"></div>
        <div class="so-loading-pulse"></div>
      </div>
    } @else if (panelDataset(); as entry) {

      <!-- Description -->
      @if (entry.description || entry.name) {
        <div class="so-description">{{ entry.description ?? entry.name }}</div>
        <hr class="so-divider" />
      }

      <!-- General Information -->
      <p class="so-section-label">
        <mat-icon class="so-section-icon" fontSet="material-icons-outlined">info</mat-icon>
        {{ t('asset.dialog.generalInformation.header') }}
      </p>
      <div class="so-field-grid">
        <div class="so-field">
          <span class="so-field-label">{{ t('asset.serviceProvider.label') }}</span>
          <span class="so-field-value">{{ entry.publisher }}</span>
        </div>
        <div class="so-field">
          <span class="so-field-label">{{ t('asset.serviceOriginator.label') }}</span>
          <span class="so-field-value">{{ entry.sovereign }}</span>
        </div>
        <div class="so-field">
          <span class="so-field-label">{{ t('asset.serviceType.label') }}</span>
          <span class="so-field-value">{{ t('asset.enum.serviceType.' + entry.serviceType) }}</span>
        </div>
        <div class="so-field">
          <span class="so-field-label">{{ t('asset.language.label') }}</span>
          <span class="so-field-value">{{ t('asset.enum.language.' + entry.language) }}</span>
        </div>
      </div>

      <hr class="so-divider" />

      <!-- Technical Information -->
      <p class="so-section-label">
        <mat-icon class="so-section-icon" fontSet="material-icons-outlined">settings</mat-icon>
        {{ t('asset.dialog.technicalInformation.header') }}
      </p>
      <div class="so-field-grid">
        @if (panelBpnl()) {
          <div class="so-field">
            <span class="so-field-label">{{ t('asset.bpnl.label') }}</span>
            <span class="so-field-value so-field-value--mono">{{ panelBpnl() }}</span>
          </div>
        }
        @if (panelProtocolUrl()) {
          <div class="so-field so-field--full">
            <span class="so-field-label">{{ t('asset.connectorProtocolUrl.label') }}</span>
            <span class="so-field-value so-field-value--url">{{ panelProtocolUrl() }}</span>
          </div>
        }
        <div class="so-field">
          <span class="so-field-label">{{ t('asset.dataSource.label') }}</span>
          <span class="so-field-value">{{ t('asset.enum.dataSource.' + entry.datasource) }}</span>
        </div>
        <div class="so-field">
          <span class="so-field-label">{{ t('asset.contentType.label') }}</span>
          <span class="so-field-value so-field-value--mono">{{ entry.contenttype }}</span>
        </div>
        <div class="so-field">
          <span class="so-field-label">{{ t('asset.method.label') }}</span>
          <span class="so-field-value">{{ t('asset.enum.method.' + entry.method) }}</span>
        </div>
      </div>

      <hr class="so-divider" />

      <!-- Metadata -->
      <p class="so-section-label">
        <mat-icon class="so-section-icon" fontSet="material-icons-outlined">label</mat-icon>
        {{ t('asset.dialog.metadata.header') }}
      </p>
      <div class="so-field-grid">
        @if (entry.subCategory) {
          <div class="so-field">
            <span class="so-field-label">{{ t('asset.subCategory.label') }}</span>
            <span class="so-field-value">{{ t('asset.subCategory.' + entry.subCategory) }}</span>
          </div>
        }
      </div>
      @if (panelKeywords().length) {
        <div class="so-keywords">
          <span class="so-field-label">{{ t('asset.keywords.label') }}</span>
          <div class="so-keyword-chips">
            @for (keyword of panelKeywords(); track keyword) {
              <span class="so-chip">{{ keyword }}</span>
            }
          </div>
        </div>
      }

      @if (panelHasDataModel()) {
        <hr class="so-divider" />
        <div class="so-data-model-action">
          <button class="so-btn so-btn--secondary so-btn--block" (click)="openDataModel()">
            <mat-icon style="font-size: 18px; width: 18px; height: 18px;">account_tree</mat-icon>
            {{ t('catalog.detail.dataModel.button') }}
          </button>
        </div>
      }

      <!-- Policies -->
      @if (panelPolicyCards().length) {
        <hr class="so-divider" />
        <p class="so-section-label">
          <mat-icon class="so-section-icon" fontSet="material-icons-outlined">verified_user</mat-icon>
          {{ t('menu.policies') }}
        </p>
        <div class="so-policy-list">
          @for (card of panelPolicyCards(); track $index) {
            <div class="so-policy-item so-policy-item--{{ card.severity }}">
              <div class="so-policy-header">
                <mat-icon class="so-policy-icon" fontSet="material-icons-outlined">{{ card.icon }}</mat-icon>
                <span class="so-policy-title">{{ card.title }}</span>
                <span class="so-severity-badge so-severity-badge--{{ card.severity }}">
                  <mat-icon class="so-badge-icon" fontSet="material-icons-outlined">{{ card.badgeIcon }}</mat-icon>
                  {{ card.typeBadge }}
                </span>
              </div>
              @if (card.explanation) {
                <span class="so-policy-explanation">{{ card.explanation }}</span>
              }
              @if (card.constraintRows.length) {
                <div class="so-constraint-rows">
                  @for (row of card.constraintRows; track $index) {
                    <div class="so-constraint-row">
                      <span class="so-constraint-label">{{ row.label }}</span>
                      <span class="so-constraint-value" [class.so-constraint-value--mono]="row.mono">{{ row.value }}</span>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    }
  </ng-container>
</ng-template>

<!-- ═══════════════════════════════════════════════════════════ -->
<!--  Slide-Over Panel: Footer                                  -->
<!-- ═══════════════════════════════════════════════════════════ -->
<!-- TODO: Verhandlungs-Button vorerst deaktiviert – wird aktuell nicht benötigt -->
<ng-template #panelFooter>
</ng-template>
